import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, ArrowUp, ArrowDown, Minus, ShieldCheck, ShieldAlert, Download, Loader2 } from "lucide-react";
import { endpoints } from "@/lib/api";
import { usePersona } from "@/lib/personaStore";
import { displayReqNumber } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

// Deterministic compliance % per asset tag — mocks what a real Endpoint
// Management / MDM feed (Intune, BigFix, Jamf) would return for the device.
function complianceForAsset(tag) {
  const s = tag || "";
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return 60 + (h % 41); // 60 – 100
}

const LOW_COMPLIANCE_THRESHOLD = 80;

function DexBadge({ score, trend, size = "md" }) {
  const color = score >= 85 ? { bg: "#DCFCE7", fg: "#166534" } : score >= 70 ? { bg: "#FEF3C7", fg: "#92400E" } : { bg: "#FEE2E2", fg: "#991B1B" };
  const Icon = trend === "up" ? ArrowUp : trend === "down" ? ArrowDown : Minus;
  return (
    <span data-testid={`dex-badge-${score}`} className={`inline-flex items-center gap-1 font-mono font-bold rounded ${size === "sm" ? "text-[11px] px-1.5 py-0.5" : "text-xs px-2 py-0.5"}`} style={{ backgroundColor: color.bg, color: color.fg }}>
      <Activity size={12} /> {score}
      <Icon size={10} />
    </span>
  );
}

function Signal({ label, value, unit, danger }) {
  return (
    <div className={`border rounded-md p-3 ${danger ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`font-display font-black text-xl mt-1 ${danger ? "text-[#DA291C]" : "text-slate-900"}`}>{value}<span className="text-xs font-normal text-slate-500 ml-1">{unit}</span></div>
    </div>
  );
}

export default function DeviceHealthDrawer({ assetTag, open, onOpenChange, onChanged }) {
  const { persona, userName, userEmail } = usePersona();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [healing, setHealing] = useState(null);

  const load = () => {
    if (!assetTag) return;
    setLoading(true);
    endpoints.dexDevice(assetTag).then((d) => { setData(d); setLoading(false); });
  };

  useEffect(() => {
    if (open && assetTag) {
      load();
    } else if (!open) {
      setData(null);
    }
  }, [open, assetTag]);

  const handleOpen = (o) => {
    onOpenChange(o);
  };

  const runHeal = async (action) => {
    setHealing(action.id);
    try {
      const res = await endpoints.selfHeal({ asset_tag: assetTag, action_id: action.id, persona_id: persona.id, user_name: userName, user_email: userEmail || null });
      toast.success(`${action.label} triggered — Ticket ${displayReqNumber(res.ticket)}`);
      load();
      onChanged?.();
    } catch { toast.error("Could not trigger action"); }
    finally { setHealing(null); }
  };

  const signals = data?.dex_signals || {};
  const compliancePct = complianceForAsset(assetTag);
  const lowCompliance = compliancePct < LOW_COMPLIANCE_THRESHOLD;
  const [complianceState, setComplianceState] = useState("idle"); // idle | downloading | applying | done
  const [effectiveCompliance, setEffectiveCompliance] = useState(compliancePct);

  useEffect(() => {
    // Reset local compliance UI whenever a different device is opened.
    setComplianceState("idle");
    setEffectiveCompliance(compliancePct);
  }, [assetTag, compliancePct]);

  const runComplianceScript = async () => {
    if (complianceState !== "idle" && complianceState !== "done") return;
    setComplianceState("downloading");
    toast.loading("Downloading compliance patch bundle…", { id: "compliance-run" });
    await new Promise((r) => setTimeout(r, 1400));
    setComplianceState("applying");
    toast.loading("Applying patches · restarting services…", { id: "compliance-run" });
    await new Promise((r) => setTimeout(r, 1800));
    setEffectiveCompliance(98);
    setComplianceState("done");
    toast.success(`Device compliance restored to 98%`, { id: "compliance-run" });
  };

  return (
    <Sheet open={open} onOpenChange={handleOpen}>
      <SheetContent side="right" className="w-full sm:max-w-lg bg-white overflow-y-auto" data-testid="device-health-drawer">
        <SheetHeader>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Nexthink Device UX</div>
          <SheetTitle className="font-display text-2xl text-slate-900">Device Health</SheetTitle>
          {data && <SheetDescription>{data.device_name} · <span className="font-mono">{data.asset_tag}</span></SheetDescription>}
        </SheetHeader>

        {loading && <div className="mt-6 space-y-3"><Skeleton className="h-24 w-full" /><Skeleton className="h-40 w-full" /></div>}

        {data && !loading && (
          <div className="mt-5 space-y-5">
            <div className="border border-slate-200 rounded-md p-5 bg-slate-50 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Digital Employee Experience</div>
                <div className="font-display font-black text-5xl text-slate-900 mt-1">{data.dex_score}<span className="text-lg text-slate-500 font-normal">/100</span></div>
                <div className="text-xs text-slate-600 mt-1">Trend: {data.dex_trend} · Age: {data.age_months} months · {data.refresh_status}</div>
              </div>
              <DexBadge score={data.dex_score} trend={data.dex_trend} />
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Live Signals</div>
              <div className="grid grid-cols-2 gap-2" data-testid="dex-signals">
                <Signal label="Crash rate" value={signals.crash_rate_per_week} unit="/wk" danger={signals.crash_rate_per_week > 1} />
                <Signal label="Boot time" value={signals.boot_time_seconds} unit="s" danger={signals.boot_time_seconds > 30} />
                <Signal label="Wi-Fi drops (24h)" value={signals.wifi_drops_24h} unit="" danger={signals.wifi_drops_24h > 5} />
                <Signal label="Battery health" value={signals.battery_health_pct} unit="%" danger={signals.battery_health_pct < 75} />
                <Signal label="Memory pressure" value={signals.memory_pressure_pct} unit="%" danger={signals.memory_pressure_pct > 70} />
                <Signal label="CPU sluggish" value={signals.cpu_sluggish_pct} unit="%" danger={signals.cpu_sluggish_pct > 40} />
                <Signal label="Device Compliance" value={effectiveCompliance} unit="%" danger={effectiveCompliance < LOW_COMPLIANCE_THRESHOLD} />
              </div>
            </div>

            {data.top_issues?.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Top Issues</div>
                <ul className="space-y-1.5">
                  {data.top_issues.map((i) => (
                    <li key={i.key} className="flex items-center gap-2 text-sm">
                      <span className={`w-1.5 h-1.5 rounded-full ${i.severity === "high" ? "bg-[#DA291C]" : "bg-amber-500"}`} />
                      <span className="text-slate-700">{i.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Self-Heal Actions</div>
              <div className="grid gap-2" data-testid="self-heal-actions">
                {data.self_heal_actions.map((a) => (
                  <Button
                    key={a.id}
                    variant="outline"
                    disabled={healing === a.id}
                    onClick={() => runHeal(a)}
                    data-testid={`heal-${a.id}`}
                    className="justify-between h-auto py-3 border-slate-200 hover:border-[#0033A0] hover:bg-[#0033A0]/5"
                  >
                    <div className="text-left">
                      <div className="font-semibold text-slate-900">{a.label}</div>
                      <div className="text-[11px] text-slate-500">~{a.duration_seconds}s · risk: {a.risk}</div>
                    </div>
                    <span className="text-xs text-[#0033A0] font-semibold">Run</span>
                  </Button>
                ))}
              </div>
              <div className="text-[11px] text-slate-500 mt-2">Actions dispatched to endpoint via Nexthink Collector. Each run creates a HEAL ticket.</div>
            </div>

            {lowCompliance && (
              <div
                data-testid="compliance-fix-panel"
                className={`rounded-md border p-4 ${
                  complianceState === "done"
                    ? "border-green-200 bg-green-50"
                    : "border-amber-300 bg-amber-50"
                }`}
              >
                <div className="flex items-start gap-3">
                  {complianceState === "done" ? (
                    <ShieldCheck size={20} className="text-green-700 shrink-0 mt-0.5" />
                  ) : (
                    <ShieldAlert size={20} className="text-amber-700 shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className={`font-semibold ${complianceState === "done" ? "text-green-900" : "text-amber-900"}`}>
                      {complianceState === "done"
                        ? "Compliance restored"
                        : `Device compliance is low (${effectiveCompliance}%)`}
                    </div>
                    <div className={`text-xs mt-1 ${complianceState === "done" ? "text-green-800" : "text-amber-800"}`}>
                      {complianceState === "done"
                        ? "All required patches were downloaded and applied. The endpoint is back in policy."
                        : "Endpoint is missing 3 policy-required patches (KB5039212 · Chrome 128 · CrowdStrike sensor). Run the fix script to auto-download and apply them via BigFix."}
                    </div>
                    <Button
                      data-testid="run-compliance-script-btn"
                      onClick={runComplianceScript}
                      disabled={complianceState === "downloading" || complianceState === "applying"}
                      className={`mt-3 h-8 text-xs font-semibold ${
                        complianceState === "done"
                          ? "bg-green-700 hover:bg-green-800 text-white"
                          : "bg-[#0033A0] hover:bg-[#002573] text-white"
                      }`}
                    >
                      {complianceState === "downloading" && (
                        <><Loader2 size={12} className="mr-1.5 animate-spin" /> Downloading patch…</>
                      )}
                      {complianceState === "applying" && (
                        <><Loader2 size={12} className="mr-1.5 animate-spin" /> Applying patches…</>
                      )}
                      {complianceState === "idle" && (
                        <><Download size={12} className="mr-1.5" /> Run compliance fix script</>
                      )}
                      {complianceState === "done" && (
                        <><ShieldCheck size={12} className="mr-1.5" /> Re-run fix script</>
                      )}
                    </Button>
                    <div className="text-[10px] text-slate-500 mt-2 font-mono">
                      BigFix Fixlet #4127 · flexspace/scripts/compliance-baseline.ps1
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export { DexBadge };
